import { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import {
  AlertTriangle,
  ShieldAlert,
  Phone,
  HeartPulse,
  Download,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { generateDoctorReport } from "@/lib/doctorReport";

export default function EmergencyView() {
  const { profiles } = useOutletContext();
  const [selectedId, setSelectedId] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!selectedId && profiles?.length > 0) setSelectedId(profiles[0].id);
  }, [profiles, selectedId]);

  const load = async () => {
    if (!selectedId) return;
    setError("");
    const profile = profiles.find((p) => p.id === selectedId);
    if (!profile) return;
    try {
      const [meds, vaccs, growth, appts, ins, contacts, episodes] = await Promise.all([
        supabase.from("medications").select("*").eq("profile_id", selectedId).order("created_at", { ascending: false }),
        supabase.from("vaccinations").select("*").eq("profile_id", selectedId).order("date_administered", { ascending: false }),
        supabase.from("growth_measurements").select("*").eq("profile_id", selectedId).order("measurement_date", { ascending: false }),
        supabase.from("appointments").select("*").eq("profile_id", selectedId).order("appointment_date", { ascending: false }),
        supabase.from("insurance_info").select("*").eq("profile_name", profile.name),
        supabase.from("contacts").select("*").eq("profile_id", selectedId),
        supabase.from("sick_episodes").select("*").eq("profile_id", selectedId).order("start_date", { ascending: false }),
      ]);

      setSnapshot({
        generated_at: new Date().toISOString(),
        profile,
        medications: meds.data || [],
        vaccinations: vaccs.data || [],
        growth: growth.data || [],
        appointments: appts.data || [],
        insurance: ins.data || [],
        contacts: contacts.data || [],
        sick_episodes: episodes.data || [],
      });
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    if (selectedId) load();
    // eslint-disable-next-line
  }, [selectedId]);

  const generateReport = async () => {
    if (!snapshot) return;
    setReportLoading(true);
    try {
      await generateDoctorReport(snapshot);
    } finally {
      setReportLoading(false);
    }
  };

  const profile = profiles?.find((p) => p.id === selectedId);

  if (!profiles || profiles.length === 0) {
    return (
      <div className="space-y-6" data-testid="emergency-needs-profile">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Emergency View</h1>
        <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border bg-card/50 p-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10">
            <AlertTriangle className="h-7 w-7 text-destructive" />
          </div>
          <h2 className="mt-4 font-heading text-xl font-bold">Add a profile first</h2>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            Emergency cards and doctor reports are generated per family member.
          </p>
          <Link to="/profiles">
            <Button className="mt-6">Go to profiles</Button>
          </Link>
        </div>
      </div>
    );
  }

  const activeMeds = snapshot?.medications?.filter((m) => m.is_active) || [];
  const emergencyContacts = snapshot?.contacts?.filter((c) => c.is_emergency) || [];
  const insurance = snapshot?.insurance || [];

  return (
    <div className="mx-auto max-w-5xl space-y-8" data-testid="emergency-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-destructive">
            Critical Info
          </span>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight">
            Emergency & Reports
          </h1>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            One-screen emergency view + doctor-ready PDF report for any visit.
          </p>
        </div>
        <div className="flex items-center gap-3 rounded-xl bg-card px-3 py-1.5 shadow-soft">
          <span className="text-xs text-muted-foreground">Profile</span>
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="h-7 w-40 border-0 bg-transparent shadow-none" data-testid="emergency-profile-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {profiles.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-3xl bg-destructive p-6 text-destructive-foreground shadow-card sm:p-8" data-testid="emergency-card-preview">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest">
            <ShieldAlert className="h-4 w-4" /> Emergency Card
          </div>
          <h2 className="mt-3 font-heading text-3xl font-extrabold">
            {profile?.name}
          </h2>
          <div className="mt-1 text-sm text-destructive-foreground/80">
            {profile?.type && <span className="capitalize">{profile.type}</span>}
            {profile?.birthdate && ` · ${profile.birthdate}`}
            {profile?.blood_type && ` · Blood ${profile.blood_type}`}
          </div>

          <div className="mt-6 space-y-3">
            <Block label="Allergies" icon={AlertTriangle}>
              {profile?.allergies?.length > 0 ? profile.allergies.join(", ") : "None reported"}
            </Block>
            <Block label="Active medications" icon={HeartPulse}>
              {activeMeds.length > 0
                ? activeMeds.map((m) => `${m.name} ${m.dosage}`).join(" · ")
                : "None"}
            </Block>
            <Block label="Emergency contacts" icon={Phone}>
              {emergencyContacts.length > 0
                ? emergencyContacts.slice(0, 3).map((c) => `${c.name}${c.phone ? ` (${c.phone})` : ""}`).join(" · ")
                : "Mark a contact as ‘emergency’ to populate"}
            </Block>
            {insurance.length > 0 && (
              <Block label="Insurance" icon={ShieldAlert}>
                {insurance.map((i) => `${i.provider_name}${i.member_id ? ` · ID ${i.member_id}` : ""}`).join(" · ")}
              </Block>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl bg-card p-6 shadow-soft" data-testid="emergency-report-section">
            <h3 className="font-heading text-lg font-bold">Generate doctor report</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              A printable PDF with medical history, current meds, vaccinations, growth, and recent visits — ready for any appointment.
            </p>
            <Button
              onClick={generateReport}
              disabled={reportLoading || !snapshot}
              className="mt-4 bg-primary text-primary-foreground"
              data-testid="emergency-generate-report"
            >
              <Download className="mr-1 h-4 w-4" />
              {reportLoading ? "Building PDF…" : "Download PDF report"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Block = ({ label, children, icon: Icon }) => (
  <div>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-destructive-foreground/70">
      <Icon className="h-3 w-3" />
      {label}
    </div>
    <div className="mt-1 text-sm font-medium">{children}</div>
  </div>
);
